import classNames from "classnames"
import React, { ComponentProps } from "react"

import Typography, { TypographySize } from "./Typography"
import { Color } from "../../../constants/V2/color"
import { formatPrice } from "../../../utils/price"
import { ProductPrice } from "../../../utils/shop"

type Props = {
  productPrices: ProductPrice
  onPromotion?: boolean
  priceFontWeight?: ComponentProps<typeof Typography>["weight"]
  originalPriceLabel?: string
  originalPriceColor?: Color
  className?: string
  priceFontSize?: TypographySize
  superscriptFontsize?: TypographySize
  subscriptText?: string
  priceColor?: Color
  showDecimals?: boolean
  overrideShowDiscount?: boolean
  showSavingsPercentage?: boolean
  orignalPricePosition?: "top" | "right"
}

const PriceDisplay = ({
  productPrices,
  onPromotion,
  className,
  originalPriceLabel,
  originalPriceColor = Color.Charcoal,
  priceFontWeight = "book",
  priceFontSize = "body-md",
  superscriptFontsize = "subscript-sm",
  subscriptText,
  priceColor = Color.Charcoal,
  showDecimals = true,
  overrideShowDiscount = false,
  showSavingsPercentage = false,
  orignalPricePosition = "top",
}: Props) => {
  const displayPrice = productPrices
    ? formatPrice(productPrices.price, {
        shouldShowDecimals: showDecimals,
        hasThousandsSeparator: true,
      })
    : ""

  //TODO see if this should be default for all prices
  const savingsAmount = productPrices.originalPrice
    ? parseFloat(productPrices.originalPrice) - parseFloat(productPrices.price)
    : 0

  const percentageSaved =
    productPrices.originalPrice && savingsAmount > 0
      ? Math.round(
          (savingsAmount / parseFloat(productPrices.originalPrice)) * 100
        )
      : 0

  const displayOriginalPrice = productPrices.originalPrice
    ? formatPrice(productPrices.originalPrice, {
        shouldShowDecimals: showDecimals,
        hasThousandsSeparator: true,
      })
    : ""

  return (
    <div
      className={classNames(
        "flex flex-col items-start lg:items-start",
        className
      )}
    >
      {onPromotion && orignalPricePosition == "top" && (
        <div className="flex flex-row uppercase">
          {originalPriceLabel && (
            <Typography
              color={originalPriceColor}
              text={originalPriceLabel}
              font="grotesk"
              size={superscriptFontsize}
              weight="book"
              className={classNames("pr-4", {
                "opacity-40": !overrideShowDiscount,
              })}
            />
          )}
          <Typography
            color={originalPriceColor}
            text={
              overrideShowDiscount
                ? formatPrice(savingsAmount.toString())
                : displayOriginalPrice
            }
            font="grotesk"
            size={superscriptFontsize}
            weight="book"
            className={classNames(
              !overrideShowDiscount && "line-through opacity-40"
            )}
          />
        </div>
      )}
      <div className="flex items-center gap-8">
        <div className="flex items-center gap-4">
          <Typography
            color={priceColor}
            text={displayPrice}
            font="grotesk"
            size={priceFontSize}
            weight={priceFontWeight}
          />
          {onPromotion && orignalPricePosition == "right" && (
            <div className="flex flex-row uppercase">
              {originalPriceLabel && (
                <Typography
                  color={originalPriceColor}
                  text={originalPriceLabel}
                  font="grotesk"
                  size={superscriptFontsize}
                  weight="book"
                  className={classNames("pr-4", {
                    "opacity-40": !overrideShowDiscount,
                  })}
                />
              )}
              <Typography
                color={originalPriceColor}
                text={
                  overrideShowDiscount
                    ? formatPrice(savingsAmount.toString())
                    : displayOriginalPrice
                }
                font="grotesk"
                size={superscriptFontsize}
                weight="book"
                className={classNames(
                  !overrideShowDiscount && "line-through opacity-40"
                )}
              />
            </div>
          )}
        </div>

        {showSavingsPercentage && savingsAmount > 0 && (
          <div className="mb-[2px] flex h-fit items-center justify-center rounded-24 bg-orange/15 px-8 py-[2px]">
            <Typography
              text={`Save ${percentageSaved}%`}
              size="body-sm"
              font="grotesk"
              weight="book"
              color={Color.Orange}
            />
          </div>
        )}
      </div>
      {subscriptText && (
        <Typography
          color={priceColor}
          weight="book"
          font="grotesk"
          size={superscriptFontsize}
          text={subscriptText}
          className={classNames("-mt-8 text-left lg:mt-0")}
        />
      )}
    </div>
  )
}

export default PriceDisplay
